import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ApContentService } from '@ap/content';
import { ApDarkmodeService } from '@ap/darkmode';
import { ErrorMessages } from '@ap/form/errors';

import { MoodOptions } from '../../mijn-feedback.constants';
import genericContent from '../../mijn-feedback.content.json';
import { FeedbackOptions } from '../feedback.model';

import content from './form.content.json';
import { FeedbackForm } from './form.model';

/**
 * @module FeedbackFormModule
 * @ngdoc component
 * @name FeedbackFormComponent
 */
@Component({
    selector: 'mijn-feedback-form',
    templateUrl: 'form.component.html',
    styleUrls: ['./form.component.scss'],
    standalone: false
})
export class FeedbackFormComponent implements OnInit, OnDestroy {
    @Input({ required: true })
    public moodTitle: string;

    @Input({ required: true })
    public mood: string;

    @Output()
    public feedback: EventEmitter<FeedbackOptions> = new EventEmitter();

    @Output()
    public cancel: EventEmitter<void> = new EventEmitter();

    @Output()
    public toContact: EventEmitter<void> = new EventEmitter();

    public feedbackForm: FormGroup<FeedbackForm>;
    public commentsErrorMessages: ErrorMessages;
    public moodErrorMessages: ErrorMessages;
    public moodOptions = MoodOptions;
    public maxCharacters = 500;
    public commentsTitle;
    public modeSetting = 'light';

    private subscription = new Subscription();

    public constructor(
        private readonly contentService: ApContentService,
        private readonly darkmodeService: ApDarkmodeService
    ) {}

    public ngOnInit(): void {
        this.contentService.addContentItems(genericContent);
        this.contentService.addContentItems(content);
        this.feedbackForm = this.getFeedbackForm();

        this.determineCommentsTitle(this.mood);

        this.modeSetting = this.darkmodeService.getDarkmodeSetting();

        this.subscription.add(this.feedbackForm.controls.mood.valueChanges.subscribe((mood) => this.determineCommentsTitle(mood)));
        this.commentsErrorMessages = {
            maxlength: this.contentService.get('mijn-feedback.feedbackForm.comments.error')
        };
        this.moodErrorMessages = {
            required: this.contentService.get('mijn-feedback.feedbackForm.mood.error')
        };
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public onCancel(): void {
        this.cancel.emit();
    }

    public onContact(): void {
        this.toContact.emit();
    }

    public getSvgSource(svgName: string) {
        return `/icon/mood-icon-${svgName}-${this.modeSetting}.svg`;
    }

    public submitForm(): void {
        if (this.feedbackForm.valid) {
            this.feedback.emit(this.feedbackForm.getRawValue());
        } else {
            Object.keys(this.feedbackForm.controls).forEach((field) => {
                const control = this.feedbackForm.get(field);
                control.markAsTouched();
                control.markAsDirty();
            });
        }
    }

    public getFormControl(identifier: string): FormControl {
        if (this.feedbackForm) {
            return this.feedbackForm.get(identifier) as FormControl;
        }
        return null;
    }

    public onMoodChange(mood: string): void {
        this.getFormControl('mood').setValue(mood);
    }

    public hasMoodValue(): boolean {
        return this.getFormControl('mood').value != null;
    }

    public hasMoodError(): boolean {
        return this.getFormControl('mood').errors != null;
    }

    public getAriaLabelComments(): string {
        return `${this.commentsTitle} ${this.contentService.get('mijn-feedback.feedbackForm.info')}`;
    }

    public getAriaLabelContactButton(): string {
        return `${this.contentService.get('mijn-feedback.feedbackForm.button-info')} ${this.contentService.get(
            'mijn-feedback.feedbackForm.button-text'
        )}`;
    }

    private getFeedbackForm(): FormGroup<FeedbackForm> {
        return new FormGroup({
            mood: new FormControl<string>(this.mood, Validators.required),
            comments: new FormControl<string>('', [Validators.maxLength(this.maxCharacters)])
        });
    }

    private determineCommentsTitle(moodScore: string) {
        switch (moodScore) {
            case '1':
            case '2':
                this.commentsTitle = this.contentService.get('mijn-feedback.form.commentstitle.negative');
                break;
            case '3':
                this.commentsTitle = this.contentService.get('mijn-feedback.form.commentstitle.neutral');
                break;
            case '4':
            case '5':
                this.commentsTitle = this.contentService.get('mijn-feedback.form.commentstitle.positive');
                break;
            default:
                this.commentsTitle = '';
        }
    }
}
